// src/pages/Purchase/PurchaseOrderRecord/components/PurchaseOrderRecordTable.js
import React, { useState } from "react";
import { Table, Button, Tooltip, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePurchaseOrder,
  fetchPurchaseOrderRecord,
} from "../PurchaseOrderRecordThunk";
import { formatToCompanyTimezone } from "../../../../../lib/functions";
import { useIsAdminUser } from "../../../../../lib/accessList";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { useTxLock } from "../../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../../components/common/VoucherPdfActions";

const PurchaseOrderRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector((state) => state.purchaseOrderRecord);
  const authInfo = useSelector(selectAuthInfo);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  // const [isAdminUser, isAdminUserSet] = useState(false);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  // useEffect(() => {
  //   const hrRoles = [
  //     // Admin role
  //     "ForthERP_Basic_admin",
  //     "ForthERP_Standard_admin",
  //     "ForthERP_Pro_admin",
  //     "PharmaERP_Basic_admin",
  //     "PharmaERP_Standard_admin",
  //     "PharmaERP_Pro_admin",
  //     "PharmaBooks_Basic_admin",
  //     "PharmaBooks_Standard_admin",
  //     "PharmaBooks_Pro_admin",
  //     "ForthBooks_Basic_admin",
  //     "ForthBooks_Standard_admin",
  //     "ForthBooks_Pro_admin",
  //     "HRPayroll_Standard_admin",
  //     "HRPayroll_Pro_admin",
  //     "SuperERP_admin",
  //     "SuperERP_super_admin",
  //   ];

  //   const userRoles = authInfo.userInfo.user_role_names
  //     ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
  //     : [];

  //   const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

  //   isAdminUserSet(isAdminHR);
  // }, [authInfo]);

  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const navigate = useNavigate();
  const dispatch = useDispatch();





  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Supplier", dataIndex: "supplier" },
    { title: "Created By", dataIndex: "createdBy" },
    {
      title: "Sub Total",
      dataIndex: "subtotal",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      render: (val) => Number(val).toFixed(2),
    },
    showTax && {
      title: taxLabel,
      dataIndex: "vat",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Service",
      dataIndex: "service",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => {

        switch (status) {
          case "Approved":
            return <Tag color="green">Approved ✅</Tag>;

          case "Pending":
            // If partially delivered (remaining qty > 0 but not full)
            if (
              Number(record.remainingQty) > 0 &&
              Number(record.remainingQty) < Number(record.totalQty)
            ) {
              return <Tag color="orange">Partially Delivered ⏳</Tag>;
            }
            return <Tag color="blue">Pending ⏳</Tag>;

          case "Cancelled":
            return <Tag color="red">Cancelled ❌</Tag>;

          default:
            return <Tag color="default">{status}</Tag>;
        }
      },
    },
    {
      // Separate from "Status" above (open vs. fully-received) — this is
      // the multi-stage approval workflow's own state, read directly off
      // the new approval_status column rather than through the existing
      // status mapping (which has no room for a third value).
      title: "Approval",
      dataIndex: "approval_status",
      render: (approvalStatus) => {
        if (approvalStatus === "pending") return <Tag color="gold">Pending Approval</Tag>;
        if (approvalStatus === "approved") return <Tag color="green">Approved</Tag>;
        return null;
      },
    },
    // {
    //   title: "Paid",
    //   dataIndex: "paid",
    //   render: (val) => Number(val).toFixed(2),
    // },
    // {
    //   title: "Due",
    //   dataIndex: "due",
    //   render: (val) => Number(val).toFixed(2),
    // },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => {

        const txLocked = isTxLocked(record?.date);
        const handleDelete = async () => {
          if (record.status === "Approved") {
            Swal.fire(
              "Not Allowed",
              "Approved orders cannot be deleted",
              "warning",
            );
            return;
          }

          if (!isAdminUser) {
            Swal.fire("Not Allowed", "Only Admin can delete orders", "warning");
            return;
          }

          const result = await Swal.fire({
            title: "Are you sure?",
            text: "Purchase Order will be deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
          });

          if (!result.isConfirmed) return;

          const res = await dispatch(deletePurchaseOrder(record.id));

          if (res.meta.requestStatus === "fulfilled") {
            Swal.fire(
              "Deleted!",
              "Purchase Order deleted successfully",
              "success",
            );

            // OPTIONAL SAFE REFRESH
            dispatch(
              fetchPurchaseOrderRecord({
                selectedFilterType: filters.filterType,
                selectedRecordType: filters.recordType,
                filters,
              }),
            );
          }
        };

        return (
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot view yet" : "View Voucher"}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                disabled={record.approval_status === "pending"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (record.approval_status === "pending") return;
                  navigate(`/order/quick-purchase-order-voucher/${record.id}`)
                }

                }
              />
            </Tooltip>


          <Tooltip
  title={
    Number(record?.is_locked) === 1
      ? "Cannot edit. This Purchase Order is converted to Purchase"
      : txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Edit Voucher"
  }
>
  <Button
    type="link"
    icon={<EditOutlined />}
    disabled={
      !(isAdminUser || canEdit) ||
      Number(record?.is_locked) === 1 ||
      txLocked
    }
    onClick={(e) => {
      e.stopPropagation();

      if (Number(record?.is_locked) === 1 || txLocked) return;

      navigate(`/order/purchase-order-update/${record.id}`);
    }}
  />
</Tooltip>

            {record.status !== "Approved" ? (
              <>
               <Tooltip
  title={
    record.approval_status === "pending"
      ? "Pending approval — cannot convert yet"
      : txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Convert to Purchase"
  }
>
  <Button
    type="link"
    icon={<SwapOutlined />}
    disabled={txLocked || record.approval_status === "pending"}
    onClick={(e) => {
      e.stopPropagation();

      if (txLocked || record.approval_status === "pending") return;

      navigate(`/purchase/order-to-purchase/${record.id}`);
    }}
  />
</Tooltip>

               <Tooltip
  title={
    txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Delete Order"
  }
>
  <Button
    type="link"
    danger
    disabled={!(isAdminUser || canDelete) || txLocked}
    icon={<DeleteOutlined />}
    onClick={handleDelete}
  />
</Tooltip>

              </>
            ) : (
              <Tooltip title="Order Approved - Locked">
                <LockOutlined
                  style={{ fontSize: 18, color: "#bfbfbf", marginLeft: 8 }}
                />
              </Tooltip>
            )}

            <VoucherPdfActions
              url={`/print/purchase-order/${record.id}`}
              fileName={`PurchaseOrder_${record.vch_no || record.id}.pdf`}
              disabled={record.approval_status === "pending"}
              disabledTitle="Pending approval — cannot share or download yet"
            />
          </div>
        );
      },
    },
    // !printMode && {
    //   title: "Actions",
    //   className: "no-print",
    //   render: () => (
    //     <Button type="link" icon={<PrinterOutlined style={{ color: "green" }} />} />
    //   ),
    // },
  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Supplier", dataIndex: "supplier" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    { title: "Rate", dataIndex: "rate" },
    ...(list.some((row) => row.discount && row.discount !== "0%")
      ? [{ title: "Discount%", dataIndex: "discount" }]
      : []),
    ...(showTax && list.some((row) => row.vat && row.vat !== "0%")
      ? [{ title: "VAT%", dataIndex: "vat" }]
      : []),
    { title: "Qty", dataIndex: "qty" },
    { title: "Total", dataIndex: "total" },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => {
        const handleDelete = async () => {
          if (record.status === "Approved") {
            Swal.fire(
              "Not Allowed",
              "Approved orders cannot be deleted",
              "warning",
            );
            return;
          }

          const result = await Swal.fire({
            title: "Are you sure?",
            text: "Purchase Order will be deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
          });

          if (!result.isConfirmed) return;

          const res = await dispatch(deletePurchaseOrder(record.row_id));

          if (res.meta.requestStatus === "fulfilled") {
            Swal.fire(
              "Deleted!",
              "Purchase Order deleted successfully",
              "success",
            );

            // OPTIONAL SAFE REFRESH
            dispatch(
              fetchPurchaseOrderRecord({
                selectedFilterType: filters.filterType,
                selectedRecordType: filters.recordType,
                filters,
              }),
            );
          }
        };


        return (
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot view yet" : "View Voucher"}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                disabled={record.approval_status === "pending"}
                onClick={() => {
                  if (record.approval_status === "pending") return;
                  navigate(
                    `/order/quick-purchase-order-voucher/${record.row_id}`,
                  );
                }}
              />
            </Tooltip>

            {record.status !== "Approved" ? (
              <>
                <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot convert yet" : "Convert to Purchase"}>
                  <Button
                    type="link"
                    icon={<SwapOutlined />}
                    disabled={record.approval_status === "pending"}
                    onClick={() => {
                      if (record.approval_status === "pending") return;
                      navigate(`/purchase/order-to-purchase/${record.row_id}`);
                    }}
                  />
                </Tooltip>

                <Tooltip title="Delete Order">
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                  />
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Order Approved - Locked">
                <LockOutlined
                  style={{ fontSize: 18, color: "#bfbfbf", marginLeft: 8 }}
                />
              </Tooltip>
            )}

            <VoucherPdfActions
              url={`/print/purchase-order/${record.row_id}`}
              fileName={`PurchaseOrder_${record.vch_no || record.row_id}.pdf`}
              disabled={record.approval_status === "pending"}
              disabledTitle="Pending approval — cannot share or download yet"
            />
          </div>
        );
      },
    },
  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;
  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.masterQty || 0);
        acc.base_qty += Number(row.baseQty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 },
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.subtotal += Number(row.subtotal || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.service += Number(row.service || 0);
        acc.total += Number(row.total || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
        return acc;
      },
      {
        subtotal: 0,
        discount: 0,
        vat: 0,
        service: 0,
        total: 0,
        paid: 0,
        due: 0,
      },
    );
  }

  // -----------------------
  // Expandable rows (for WITH ITEM)
  // -----------------------
  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  return (
    <Table
      rowKey="id"
      columns={
        filters.recordType === "with item" ? withItemColumns : summaryColumns
      }
      data-testid="purchase-order-record-table"
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={9}>
                <strong>
                  Grand Total : Master U. Qty = {totals.master_qty}, Base U. Qty
                  = {totals.base_qty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9}>
                <strong>{totals.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={10}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                {totals.subtotal.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                {totals.discount.toFixed(2)}
              </Table.Summary.Cell>
              {showTax && (
                <Table.Summary.Cell index={7}>
                  {totals.vat.toFixed(2)}
                </Table.Summary.Cell>
              )}
              <Table.Summary.Cell index={showTax ? 8 : 7}>
                {totals.service.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={showTax ? 9 : 8}>
                {totals.total.toFixed(2)}
              </Table.Summary.Cell>
              {/* <Table.Summary.Cell index={10}>{totals.paid.toFixed(2)}</Table.Summary.Cell>
              <Table.Summary.Cell index={11}>{totals.due.toFixed(2)}</Table.Summary.Cell> */}
              {!printMode && (
                <Table.Summary.Cell index={showTax ? 12 : 11}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default PurchaseOrderRecordTable;
